import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { markLoggedIn, setCsrfToken } from '../lib/authState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActiverProfil = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const doctorId = searchParams.get('doctor_id');
  const [status, setStatus] = useState('ready');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!token || !doctorId) setStatus('error');
  }, [token, doctorId]);

  const handleActivate = async () => {
    if (!password || password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Le mot de passe doit contenir au moins une majuscule');
      return;
    }
    if (!/[a-z]/.test(password)) {
      toast.error('Le mot de passe doit contenir au moins une minuscule');
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('Le mot de passe doit contenir au moins un chiffre');
      return;
    }
    setStatus('loading');
    try {
      const response = await axios.post(API_URL + '/api/doctors/activate-claim',
        { token, doctor_id: doctorId, password },
        { withCredentials: true }
      );
      // Correctif : cet appel pose un cookie de session valide (comme
      // /login), mais cet etat n'etait jamais reporte cote client. Sans ca,
      // la page suivante (dashboard medecin) croit l'utilisateur deconnecte :
      // aucun jeton CSRF n'est disponible, et la premiere sauvegarde du
      // medecin (planning) echoue avec une erreur CSRF.
      markLoggedIn();
      setCsrfToken(response.data?.csrf_token);
      setStatus('success');
      // Un etablissement (cabinet, clinique, centre) obtient un espace
      // clinique, pas un compte medecin : l'envoyer vers /doctor/dashboard le
      // deposerait sur un tableau de bord qui ne lui correspond pas et ou il
      // ne trouverait ni son equipe ni sa vitrine. Le backend renvoie
      // clinic_id dans ce cas (voir activate_claimed_profile).
      const versClinique = Boolean(response.data?.clinic_id);
      setTimeout(() => navigate(versClinique ? '/clinic/dashboard' : '/doctor/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur activation');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        {status === 'loading' && (
          <div>
            <Loader className="w-16 h-16 text-[#0596DE] mx-auto mb-4 animate-spin" />
            <p className="text-[#64748B]">Activation en cours...</p>
          </div>
        )}
        {status === 'ready' && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Activer mon profil</h2>
            <p className="text-[#64748B] mb-6">Choisissez un mot de passe pour activer votre profil Tabbibak.</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choisissez un mot de passe"
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
            />
            <p className="text-xs text-[#64748B] mt-1 mb-4">Min. 8 caractères, avec 1 majuscule, 1 minuscule et 1 chiffre</p>
            <Button onClick={handleActivate} className="w-full bg-[#0596DE] hover:bg-[#047CBA]">
              Activer mon profil
            </Button>
          </div>
        )}
        {status === 'success' && (
          <div>
            <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Profil active !</h2>
            <p className="text-[#64748B]">Redirection vers votre tableau de bord...</p>
          </div>
        )}
        {status === 'error' && (
          <div>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Lien invalide</h2>
            <p className="text-[#64748B] mb-4">Ce lien est invalide ou expire.</p>
            <Button onClick={() => navigate('/')} variant="outline">Retour</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiverProfil;
